/* eslint-disable max-lines */
export { Country } from './country-enum';

enum CalendlyLocations {
  Australia = 'Australia',
  Canada = 'Canada',
  USA = 'USA',
  UK = 'UK',
  Europe = 'Europe',
  Middle_East_And_North_Africa = 'Middle East and North Africa',
  Other = 'Other',
}

export type CountryType = {
  countryCode?: string;
  name: string;
  internationalCode?: string;
  suggested?: boolean;
  mobileLength?: number;
  location?: CalendlyLocations; // Add this field to include CalendlyLocations
  // Optional locale-specific display labels keyed by locale code, overriding the i18n library name.
  // Use 'en' as the English override (e.g. "Taiwan" instead of "Taiwan, Province of China").
  // Fallback chain: labels[locale] → labels[language] → labels['en'] (English UI only) → Intl / i18n library name
  labels?: Partial<Record<string, string>>;
};

// Declaration order only. The exported `countries` below is this list sorted in
// place, so nothing outside this module ever sees the unsorted order.
const COUNTRY_LIST: CountryType[] = [
  {
    countryCode: 'AU',
    name: 'Australia',
    internationalCode: '61',
    suggested: true,
    mobileLength: 9,
    location: CalendlyLocations.Australia,
  },
  {
    countryCode: 'CA',
    name: 'Canada',
    internationalCode: '1',
    suggested: true,
    mobileLength: 10,
    location: CalendlyLocations.Canada,
  },
  {
    countryCode: 'DE',
    name: 'Germany',
    internationalCode: '49',
    suggested: true,
    mobileLength: 10,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'FR',
    name: 'France',
    internationalCode: '33',
    suggested: true,
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'JP',
    name: 'Japan',
    internationalCode: '81',
    suggested: true,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'US',
    name: 'United States',
    internationalCode: '1',
    suggested: true,
    mobileLength: 10,
    location: CalendlyLocations.USA,
  },
  {
    countryCode: 'GB',
    name: 'United Kingdom',
    internationalCode: '44',
    suggested: true,
    mobileLength: 10,
    location: CalendlyLocations.UK,
  },
  {
    countryCode: 'AD',
    name: 'Andorra',
    internationalCode: '376',
    mobileLength: 6,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'AE',
    name: 'United Arab Emirates',
    internationalCode: '971',
    mobileLength: 9,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'AF',
    name: 'Afghanistan',
    internationalCode: '93',
    mobileLength: 9,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'AG',
    name: 'Antigua and Barbuda',
    internationalCode: '1-268',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'AI',
    name: 'Anguilla',
    internationalCode: '1-264',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'AL',
    name: 'Albania',
    internationalCode: '355',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'AM',
    name: 'Armenia',
    internationalCode: '374',
    mobileLength: 6,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'AO',
    name: 'Angola',
    internationalCode: '244',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'AQ',
    name: 'Antarctica',
    internationalCode: '672',
    mobileLength: 6,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'AR',
    name: 'Argentina',
    internationalCode: '54',
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'AS',
    name: 'American Samoa',
    internationalCode: '1-684',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'AT',
    name: 'Austria',
    internationalCode: '43',
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'AW',
    name: 'Aruba',
    internationalCode: '297',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'AX',
    name: 'Alland Islands',
    internationalCode: '358',
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'AZ',
    name: 'Azerbaijan',
    internationalCode: '994',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'BA',
    name: 'Bosnia and Herzegovina',
    internationalCode: '387',
    mobileLength: 8,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'BB',
    name: 'Barbados',
    internationalCode: '1-246',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'BD',
    name: 'Bangladesh',
    internationalCode: '880',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'BE',
    name: 'Belgium',
    internationalCode: '32',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'BF',
    name: 'Burkina Faso',
    internationalCode: '226',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'BG',
    name: 'Bulgaria',
    internationalCode: '359',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'BH',
    name: 'Bahrain',
    internationalCode: '973',
    mobileLength: 8,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'BI',
    name: 'Burundi',
    internationalCode: '257',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'BJ',
    name: 'Benin',
    internationalCode: '229',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'BL',
    name: 'Saint Barthelemy',
    internationalCode: '590',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'BM',
    name: 'Bermuda',
    internationalCode: '1-441',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'BN',
    name: 'Brunei Darussalam',
    internationalCode: '673',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'BO',
    name: 'Bolivia',
    internationalCode: '591',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'BR',
    name: 'Brazil',
    internationalCode: '55',
    mobileLength: 11,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'BS',
    name: 'Bahamas',
    internationalCode: '1-242',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'BT',
    name: 'Bhutan',
    internationalCode: '975',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'BV',
    name: 'Bouvet Island',
    internationalCode: '47',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'BW',
    name: 'Botswana',
    internationalCode: '267',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'BY',
    name: 'Belarus',
    internationalCode: '375',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'BZ',
    name: 'Belize',
    internationalCode: '501',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'CC',
    name: 'Cocos (Keeling) Islands',
    internationalCode: '61',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'CD',
    name: 'Congo, Democratic Republic of the',
    internationalCode: '243',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'CF',
    name: 'Central African Republic',
    internationalCode: '236',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'CG',
    name: 'Congo, Republic of the',
    internationalCode: '242',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'CH',
    name: 'Switzerland',
    internationalCode: '41',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'CI',
    name: "Cote d'Ivoire",
    internationalCode: '225',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'CK',
    name: 'Cook Islands',
    internationalCode: '682',
    mobileLength: 5,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'CL',
    name: 'Chile',
    internationalCode: '56',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'CM',
    name: 'Cameroon',
    internationalCode: '237',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'CN',
    name: 'China',
    internationalCode: '86',
    mobileLength: 11,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'CO',
    name: 'Colombia',
    internationalCode: '57',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'CR',
    name: 'Costa Rica',
    internationalCode: '506',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'CU',
    name: 'Cuba',
    internationalCode: '53',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'CV',
    name: 'Cape Verde',
    internationalCode: '238',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'CW',
    name: 'Curacao',
    internationalCode: '599',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'CX',
    name: 'Christmas Island',
    internationalCode: '61',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'CY',
    name: 'Cyprus',
    internationalCode: '357',
    mobileLength: 8,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'CZ',
    name: 'Czech Republic',
    internationalCode: '420',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'DJ',
    name: 'Djibouti',
    internationalCode: '253',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'DK',
    name: 'Denmark',
    internationalCode: '45',
    mobileLength: 8,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'DM',
    name: 'Dominica',
    internationalCode: '1-767',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'DO',
    name: 'Dominican Republic',
    internationalCode: '1-809',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'DZ',
    name: 'Algeria',
    internationalCode: '213',
    mobileLength: 9,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'EC',
    name: 'Ecuador',
    internationalCode: '593',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'EE',
    name: 'Estonia',
    internationalCode: '372',
    mobileLength: 8,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'EG',
    name: 'Egypt',
    internationalCode: '20',
    mobileLength: 10,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'EH',
    name: 'Western Sahara',
    internationalCode: '212',
    mobileLength: 9,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'ER',
    name: 'Eritrea',
    internationalCode: '291',
    mobileLength: 7,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'ES',
    name: 'Spain',
    internationalCode: '34',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'ET',
    name: 'Ethiopia',
    internationalCode: '251',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'FI',
    name: 'Finland',
    internationalCode: '358',
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'FJ',
    name: 'Fiji',
    internationalCode: '679',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'FK',
    name: 'Falkland Islands (Malvinas)',
    internationalCode: '500',
    mobileLength: 5,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'FM',
    name: 'Micronesia, Federated States of',
    internationalCode: '691',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'FO',
    name: 'Faroe Islands',
    internationalCode: '298',
    mobileLength: 5,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'GA',
    name: 'Gabon',
    internationalCode: '241',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'GD',
    name: 'Grenada',
    internationalCode: '1-473',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'GE',
    name: 'Georgia',
    internationalCode: '995',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'GF',
    name: 'French Guiana',
    internationalCode: '594',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'GG',
    name: 'Guernsey',
    internationalCode: '44',
    mobileLength: 10,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'GH',
    name: 'Ghana',
    internationalCode: '233',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'GI',
    name: 'Gibraltar',
    internationalCode: '350',
    mobileLength: 8,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'GL',
    name: 'Greenland',
    internationalCode: '299',
    mobileLength: 6,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'GM',
    name: 'Gambia',
    internationalCode: '220',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'GN',
    name: 'Guinea',
    internationalCode: '224',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'GP',
    name: 'Guadeloupe',
    internationalCode: '590',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'GQ',
    name: 'Equatorial Guinea',
    internationalCode: '240',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'GR',
    name: 'Greece',
    internationalCode: '30',
    mobileLength: 10,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'GS',
    name: 'South Georgia and the South Sandwich Islands',
    internationalCode: '500',
    mobileLength: 5,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'GT',
    name: 'Guatemala',
    internationalCode: '502',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'GU',
    name: 'Guam',
    internationalCode: '1-671',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'GW',
    name: 'Guinea-Bissau',
    internationalCode: '245',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'GY',
    name: 'Guyana',
    internationalCode: '592',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'HK',
    name: 'Hong Kong',
    internationalCode: '852',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'HM',
    name: 'Heard Island and McDonald Islands',
    internationalCode: '672',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'HN',
    name: 'Honduras',
    internationalCode: '504',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'HR',
    name: 'Croatia',
    internationalCode: '385',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'HT',
    name: 'Haiti',
    internationalCode: '509',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'HU',
    name: 'Hungary',
    internationalCode: '36',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'ID',
    name: 'Indonesia',
    internationalCode: '62',
    mobileLength: 11,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'IE',
    name: 'Ireland',
    internationalCode: '353',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'IL',
    name: 'Israel',
    internationalCode: '972',
    mobileLength: 9,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'IM',
    name: 'Isle of Man',
    internationalCode: '44',
    mobileLength: 10,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'IN',
    name: 'India',
    internationalCode: '91',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'IO',
    name: 'British Indian Ocean Territory',
    internationalCode: '246',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'IQ',
    name: 'Iraq',
    internationalCode: '964',
    mobileLength: 10,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'IR',
    name: 'Iran, Islamic Republic of',
    internationalCode: '98',
    mobileLength: 11,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'IS',
    name: 'Iceland',
    internationalCode: '354',
    mobileLength: 7,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'IT',
    name: 'Italy',
    internationalCode: '39',
    mobileLength: 10,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'JE',
    name: 'Jersey',
    internationalCode: '44',
    mobileLength: 10,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'JM',
    name: 'Jamaica',
    internationalCode: '1-876',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'JO',
    name: 'Jordan',
    internationalCode: '962',
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'KE',
    name: 'Kenya',
    internationalCode: '254',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'KG',
    name: 'Kyrgyzstan',
    internationalCode: '996',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'KH',
    name: 'Cambodia',
    internationalCode: '855',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'KI',
    name: 'Kiribati',
    internationalCode: '686',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'KM',
    name: 'Comoros',
    internationalCode: '269',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'KN',
    name: 'Saint Kitts and Nevis',
    internationalCode: '1-869',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'KP',
    name: "Korea, Democratic People's Republic of",
    internationalCode: '850',
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'KR',
    name: 'Korea, Republic of',
    internationalCode: '82',
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'KW',
    name: 'Kuwait',
    internationalCode: '965',
    mobileLength: 8,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'KY',
    name: 'Cayman Islands',
    internationalCode: '1-345',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'KZ',
    name: 'Kazakhstan',
    internationalCode: '7',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'LA',
    name: "Lao People's Democratic Republic",
    internationalCode: '856',
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'LB',
    name: 'Lebanon',
    internationalCode: '961',
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'LC',
    name: 'Saint Lucia',
    internationalCode: '1-758',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'LI',
    name: 'Liechtenstein',
    internationalCode: '423',
    mobileLength: 7,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'LK',
    name: 'Sri Lanka',
    internationalCode: '94',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'LR',
    name: 'Liberia',
    internationalCode: '231',
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'LS',
    name: 'Lesotho',
    internationalCode: '266',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'LT',
    name: 'Lithuania',
    internationalCode: '370',
    mobileLength: 8,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'LU',
    name: 'Luxembourg',
    internationalCode: '352',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'LV',
    name: 'Latvia',
    internationalCode: '371',
    mobileLength: 8,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'LY',
    name: 'Libya',
    internationalCode: '218',
    mobileLength: 10,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'MA',
    name: 'Morocco',
    internationalCode: '212',
    mobileLength: 9,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'MC',
    name: 'Monaco',
    internationalCode: '377',
    mobileLength: 8,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'MD',
    name: 'Moldova, Republic of',
    internationalCode: '373',
    mobileLength: 8,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'ME',
    name: 'Montenegro',
    internationalCode: '382',
    mobileLength: 8,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'MF',
    name: 'Saint Martin (French part)',
    internationalCode: '590',
    mobileLength: 6,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'MG',
    name: 'Madagascar',
    internationalCode: '261',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'MH',
    name: 'Marshall Islands',
    internationalCode: '692',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'MK',
    name: 'Macedonia, the Former Yugoslav Republic of',
    internationalCode: '389',
    mobileLength: 8,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'ML',
    name: 'Mali',
    internationalCode: '223',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'MM',
    name: 'Myanmar',
    internationalCode: '95',
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'MN',
    name: 'Mongolia',
    internationalCode: '976',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'MO',
    name: 'Macao',
    internationalCode: '853',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'MP',
    name: 'Northern Mariana Islands',
    internationalCode: '1-670',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'MQ',
    name: 'Martinique',
    internationalCode: '596',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'MR',
    name: 'Mauritania',
    internationalCode: '222',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'MS',
    name: 'Montserrat',
    internationalCode: '1-664',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'MT',
    name: 'Malta',
    internationalCode: '356',
    mobileLength: 8,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'MU',
    name: 'Mauritius',
    internationalCode: '230',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'MV',
    name: 'Maldives',
    internationalCode: '960',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'MW',
    name: 'Malawi',
    internationalCode: '265',
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'MX',
    name: 'Mexico',
    internationalCode: '52',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'MY',
    name: 'Malaysia',
    internationalCode: '60',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'MZ',
    name: 'Mozambique',
    internationalCode: '258',
    mobileLength: 12,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'NA',
    name: 'Namibia',
    internationalCode: '264',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'NC',
    name: 'New Caledonia',
    internationalCode: '687',
    mobileLength: 6,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'NE',
    name: 'Niger',
    internationalCode: '227',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'NF',
    name: 'Norfolk Island',
    internationalCode: '672',
    mobileLength: 6,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'NG',
    name: 'Nigeria',
    internationalCode: '234',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'NI',
    name: 'Nicaragua',
    internationalCode: '505',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'NL',
    name: 'Netherlands',
    internationalCode: '31',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'NO',
    name: 'Norway',
    internationalCode: '47',
    mobileLength: 8,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'NP',
    name: 'Nepal',
    internationalCode: '977',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'NR',
    name: 'Nauru',
    internationalCode: '674',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'NU',
    name: 'Niue',
    internationalCode: '683',
    mobileLength: 4,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'NZ',
    name: 'New Zealand',
    internationalCode: '64',
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'OM',
    name: 'Oman',
    internationalCode: '968',
    mobileLength: 8,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'PA',
    name: 'Panama',
    internationalCode: '507',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'PE',
    name: 'Peru',
    internationalCode: '51',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'PF',
    name: 'French Polynesia',
    internationalCode: '689',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'PG',
    name: 'Papua New Guinea',
    internationalCode: '675',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'PH',
    name: 'Philippines',
    internationalCode: '63',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'PK',
    name: 'Pakistan',
    internationalCode: '92',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'PL',
    name: 'Poland',
    internationalCode: '48',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'PM',
    name: 'Saint Pierre and Miquelon',
    internationalCode: '508',
    mobileLength: 6,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'PN',
    name: 'Pitcairn',
    internationalCode: '870',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'PR',
    name: 'Puerto Rico',
    internationalCode: '1',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'PS',
    name: 'Palestine, State of',
    internationalCode: '970',
    mobileLength: 9,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'PT',
    name: 'Portugal',
    internationalCode: '351',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'PW',
    name: 'Palau',
    internationalCode: '680',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'PY',
    name: 'Paraguay',
    internationalCode: '595',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'QA',
    name: 'Qatar',
    internationalCode: '974',
    mobileLength: 8,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'RE',
    name: 'Reunion',
    internationalCode: '262',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'RO',
    name: 'Romania',
    internationalCode: '40',
    mobileLength: 10,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'RS',
    name: 'Serbia',
    internationalCode: '381',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'RU',
    name: 'Russian Federation',
    internationalCode: '7',
    mobileLength: 10,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'RW',
    name: 'Rwanda',
    internationalCode: '250',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'SA',
    name: 'Saudi Arabia',
    internationalCode: '966',
    mobileLength: 9,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'SB',
    name: 'Solomon Islands',
    internationalCode: '677',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'SC',
    name: 'Seychelles',
    internationalCode: '248',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'SD',
    name: 'Sudan',
    internationalCode: '249',
    mobileLength: 7,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'SE',
    name: 'Sweden',
    internationalCode: '46',
    mobileLength: 7,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'SG',
    name: 'Singapore',
    internationalCode: '65',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'SH',
    name: 'Saint Helena',
    internationalCode: '290',
    mobileLength: 4,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'SI',
    name: 'Slovenia',
    internationalCode: '386',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'SJ',
    name: 'Svalbard and Jan Mayen',
    internationalCode: '47',
    mobileLength: 8,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'SK',
    name: 'Slovakia',
    internationalCode: '421',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'SL',
    name: 'Sierra Leone',
    internationalCode: '232',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'SM',
    name: 'San Marino',
    internationalCode: '378',
    mobileLength: 10,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'SN',
    name: 'Senegal',
    internationalCode: '221',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'SO',
    name: 'Somalia',
    internationalCode: '252',
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'SR',
    name: 'Suriname',
    internationalCode: '597',
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'SS',
    name: 'South Sudan',
    internationalCode: '211',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'ST',
    name: 'Sao Tome and Principe',
    internationalCode: '239',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'SV',
    name: 'El Salvador',
    internationalCode: '503',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'SX',
    name: 'Sint Maarten (Dutch part)',
    internationalCode: '1-721',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'SY',
    name: 'Syrian Arab Republic',
    internationalCode: '963',
    mobileLength: 7,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'SZ',
    name: 'Swaziland',
    internationalCode: '268',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'TC',
    name: 'Turks and Caicos Islands',
    internationalCode: '1-649',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'TD',
    name: 'Chad',
    internationalCode: '235',
    mobileLength: 6,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'TF',
    name: 'French Southern Territories',
    internationalCode: '262',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'TG',
    name: 'Togo',
    internationalCode: '228',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'TH',
    name: 'Thailand',
    internationalCode: '66',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'TJ',
    name: 'Tajikistan',
    internationalCode: '992',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'TK',
    name: 'Tokelau',
    internationalCode: '690',
    mobileLength: 5,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'TL',
    name: 'Timor-Leste',
    internationalCode: '670',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'TM',
    name: 'Turkmenistan',
    internationalCode: '993',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'TN',
    name: 'Tunisia',
    internationalCode: '216',
    mobileLength: 8,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'TO',
    name: 'Tonga',
    internationalCode: '676',
    mobileLength: 5,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'TR',
    name: 'Turkey',
    internationalCode: '90',
    mobileLength: 11,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'TT',
    name: 'Trinidad and Tobago',
    internationalCode: '1-868',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'TV',
    name: 'Tuvalu',
    internationalCode: '688',
    mobileLength: 5,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'TW',
    name: 'Taiwan, Province of China',
    // Required as the i18n library name is "Taiwan, Province of China"
    labels: { en: 'Taiwan', zh: '台灣' },
    internationalCode: '886',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'TZ',
    name: 'United Republic of Tanzania',
    internationalCode: '255',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'UA',
    name: 'Ukraine',
    internationalCode: '380',
    mobileLength: 9,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'UG',
    name: 'Uganda',
    internationalCode: '256',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'UY',
    name: 'Uruguay',
    internationalCode: '598',
    mobileLength: 8,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'UZ',
    name: 'Uzbekistan',
    internationalCode: '998',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'VA',
    name: 'Holy See (Vatican City State)',
    internationalCode: '379',
    mobileLength: 10,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'VC',
    name: 'Saint Vincent and the Grenadines',
    internationalCode: '1-784',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'VE',
    name: 'Venezuela',
    internationalCode: '58',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'VG',
    name: 'British Virgin Islands',
    internationalCode: '1-284',
    mobileLength: 7,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'VI',
    name: 'US Virgin Islands',
    internationalCode: '1-340',
    mobileLength: 10,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'VN',
    name: 'Vietnam',
    internationalCode: '84',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'VU',
    name: 'Vanuatu',
    internationalCode: '678',
    mobileLength: 5,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'WF',
    name: 'Wallis and Futuna',
    internationalCode: '681',
    mobileLength: 6,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'WS',
    name: 'Samoa',
    internationalCode: '685',
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'XK',
    name: 'Kosovo',
    internationalCode: '383',
    mobileLength: 8,
    location: CalendlyLocations.Europe,
  },
  {
    countryCode: 'YE',
    name: 'Yemen',
    internationalCode: '967',
    mobileLength: 9,
    location: CalendlyLocations.Middle_East_And_North_Africa,
  },
  {
    countryCode: 'YT',
    name: 'Mayotte',
    internationalCode: '262',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'ZA',
    name: 'South Africa',
    internationalCode: '27',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'ZM',
    name: 'Zambia',
    internationalCode: '260',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
  {
    countryCode: 'ZW',
    name: 'Zimbabwe',
    internationalCode: '263',
    mobileLength: 9,
    location: CalendlyLocations.Other,
  },
];

const priorityOrder = ['US', 'GB', 'AU', 'CA', 'NZ'];

// Custom sort function
const byPriority = (a: CountryType, b: CountryType): number => {
  let indexA = priorityOrder.indexOf(a.countryCode || '');
  let indexB = priorityOrder.indexOf(b.countryCode || '');
  indexA = indexA === -1 ? Infinity : indexA; // Assign a high index to non-priority countries
  indexB = indexB === -1 ? Infinity : indexB; // Assign a high index to non-priority countries

  // If both countries have the same priority, or lack thereof, sort them by their original order (stable sort)
  if (indexA === indexB) {
    return 0; // Keep original order if the priority is the same
  }

  // Otherwise, sort by priority
  return indexA - indexB;
};

// The sort is part of the DECLARATION, not a side effect of an exported binding.
// This package is `sideEffects: false`, so a bundler that drops an unused
// `sortedCountries` would have dropped the in-place sort with it and reordered
// every country dropdown.
export const countries: CountryType[] = COUNTRY_LIST.sort(byPriority);

// An alias, not a second sort: `Array.prototype.sort` returns its receiver, so
// `sortedCountries === countries` exactly as it always did.
export const sortedCountries = countries;

/**
 * Maps a full country name (as stored on `user.country`) to its ISO code
 */
export const getCountryCodeByName = (name?: string | null): string | undefined =>
  name ? countries.find((c) => c.name === name)?.countryCode : undefined;
